import React, { useState, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { CalendarDays, Clock, MapPin, Users, Edit2, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const CalendarView = ({ userRole = 'admin', compact = false, zone = null }) => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventDialog, setShowEventDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newEvent, setNewEvent] = useState({
    type: 'atelier',
    titre: '',
    date: '',
    heure_debut: '09:00',
    heure_fin: '10:00',
    zone_geographique: 'Saint-Pierre',
    description: ''
  });

  useEffect(() => {
    loadEvents();
  }, [zone]);

  const loadEvents = async () => {
    try {
      const params = zone ? { zone } : {};
      const [ateliersRes, actionsRes] = await Promise.all([
        api.get('/ateliers', { params }),
        api.get('/actions')
      ]);

      const calendarEvents = [];

      // Convertir les ateliers en événements calendrier
      // (vue partagée : tous les ateliers sont visibles et déplaçables par tout intervenant)
      ateliersRes.data.forEach(atelier => {
        calendarEvents.push({
          id: `atelier-${atelier.id}`,
          title: atelier.titre,
          start: `${atelier.date}T${atelier.heure_debut || '09:00'}`,
          end: `${atelier.date}T${atelier.heure_fin || '10:00'}`,
          backgroundColor: getEventColor(atelier.type_atelier),
          borderColor: getEventColor(atelier.type_atelier),
          extendedProps: {
            type: 'atelier',
            data: atelier
          }
        });
      });

      // Convertir les actions/RDV en événements calendrier
      actionsRes.data.forEach(action => {
        calendarEvents.push({
          id: `action-${action.id}`,
          title: action.titre,
          start: `${action.date}T${action.heure_debut || '09:00'}`,
          end: `${action.date}T${action.heure_fin || '10:00'}`,
          backgroundColor: '#6366f1',
          borderColor: '#6366f1',
          extendedProps: {
            type: 'action',
            data: action
          }
        });
      });

      setEvents(calendarEvents);
    } catch (error) {
      toast.error('Erreur de chargement du calendrier');
    } finally {
      setLoading(false);
    }
  };

  const getEventColor = (type) => {
    const colors = {
      'atelier': '#3b82f6',
      'job_dating': '#10b981',
      'info_collective': '#f59e0b',
      'visite_entreprise': '#8b5cf6',
      'formation': '#ec4899',
      'default': '#6366f1'
    };
    return colors[type] || colors.default;
  };

  const handleEventClick = (clickInfo) => {
    const eventData = clickInfo.event.extendedProps;
    setSelectedEvent({
      ...eventData.data,
      eventType: eventData.type,
      calendarEvent: clickInfo.event
    });
    setShowEventDialog(true);
  };

  const handleDateSelect = (selectInfo) => {
    if (userRole !== 'admin' && userRole !== 'referent' && userRole !== 'animateur') {
      return;
    }

    setNewEvent({
      ...newEvent,
      date: selectInfo.startStr.split('T')[0],
      heure_debut: '09:00',
      heure_fin: '10:00'
    });
    setShowCreateDialog(true);
  };

  // Clic simple sur un jour (utile sur la vue mois, y compris calendrier compact)
  const handleDateClick = (info) => {
    if (userRole !== 'admin' && userRole !== 'referent' && userRole !== 'animateur') {
      return;
    }

    setNewEvent({
      ...newEvent,
      date: (info.dateStr || '').split('T')[0],
      heure_debut: '09:00',
      heure_fin: '10:00'
    });
    setShowCreateDialog(true);
  };

  const handleEventDrop = async (dropInfo) => {
    // Admin et intervenant peuvent drag & drop
    if (userRole !== 'admin' && userRole !== 'animateur') {
      dropInfo.revert();
      toast.error("Vous n'êtes pas autorisé à déplacer cet événement");
      return;
    }
    
    const eventData = dropInfo.event.extendedProps;
    const newDate = dropInfo.event.startStr.split('T')[0];
    const newStart = dropInfo.event.startStr.split('T')[1]?.substring(0, 5) || '09:00';
    const newEnd = dropInfo.event.endStr?.split('T')[1]?.substring(0, 5) || '10:00';

    try {
      if (eventData.type === 'atelier') {
        await api.put(`/ateliers/${eventData.data.id}`, {
          ...eventData.data,
          date: newDate,
          heure_debut: newStart,
          heure_fin: newEnd
        });
      } else {
        // Pour les actions, on doit construire l'objet correctement
        await api.put(`/actions/${eventData.data.id}`, {
          date: newDate,
          heure_debut: newStart,
          heure_fin: newEnd
        });
      }
      
      toast.success('Événement déplacé avec succès');
      loadEvents();
    } catch (error) {
      toast.error('Erreur lors du déplacement');
      dropInfo.revert();
    }
  };

  const handleCreateEvent = async () => {
    if (!newEvent.titre || !newEvent.date) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      if (newEvent.type === 'atelier') {
        // L'intervenant connecté est rattaché par défaut comme animateur :
        // le backend exige au moins un intervenant assigné.
        const animateursIds = (userRole === 'animateur' && user?.id) ? [user.id] : [];
        await api.post('/ateliers', {
          titre: newEvent.titre,
          type_atelier: 'atelier',
          date: newEvent.date,
          heure_debut: newEvent.heure_debut,
          heure_fin: newEvent.heure_fin,
          zone_geographique: newEvent.zone_geographique,
          description: newEvent.description,
          animateurs_ids: animateursIds,
          participants: []
        });
      }
      
      toast.success('Événement créé avec succès');
      setShowCreateDialog(false);
      setNewEvent({
        type: 'atelier',
        titre: '',
        date: '',
        heure_debut: '09:00',
        heure_fin: '10:00',
        zone_geographique: 'Saint-Pierre',
        description: ''
      });
      loadEvents();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;

    try {
      if (selectedEvent.eventType === 'atelier') {
        await api.delete(`/ateliers/${selectedEvent.id}`);
      } else {
        await api.delete(`/actions/${selectedEvent.id}`);
      }
      
      toast.success('Événement supprimé');
      setShowEventDialog(false);
      setSelectedEvent(null);
      loadEvents();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatTime = (time) => {
    return time || 'N/A';
  };

  if (loading) {
    return (
      <Card className="bg-white border-slate-200 rounded-3xl shadow-sm">
        <CardContent className="p-8 text-center">
          <div className="text-slate-500">Chargement du calendrier...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="bg-white border-slate-200 rounded-3xl shadow-sm overflow-hidden">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 pb-4">
          <CardTitle className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
            <CalendarDays className="w-6 h-6 text-blue-600" />
            Calendrier des événements
          </CardTitle>
          <div className="flex flex-wrap gap-2 mt-3">
            <Badge className="bg-blue-500 text-white">Ateliers</Badge>
            <Badge className="bg-emerald-500 text-white">Job Dating</Badge>
            <Badge className="bg-amber-500 text-white">Info Collective</Badge>
            <Badge className="bg-purple-500 text-white">Visite Entreprise</Badge>
            <Badge className="bg-indigo-500 text-white">RDV Individuels</Badge>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          <style>
            {`
              /* Fix: Tailwind base reset interfère avec FullCalendar v6 en appliquant
                 content: var(--tw-content) sur tous les ::before/::after,
                 ce qui double le texte (titres, jours, numéros de date).
                 On neutralise la variable CSS Tailwind uniquement dans le scope .fc */
              .fc *::before,
              .fc *::after {
                --tw-content: unset;
              }
              .fc {
                font-family: 'DM Sans', sans-serif;
              }
              .fc .fc-toolbar-title {
                font-family: 'Nunito', sans-serif;
                font-weight: 700;
                font-size: 1.25rem;
              }
              .fc .fc-button {
                border-radius: 12px !important;
                padding: 8px 16px !important;
                font-weight: 600 !important;
                text-transform: capitalize !important;
              }
              .fc .fc-button-primary {
                background-color: #2563eb !important;
                border-color: #2563eb !important;
              }
              .fc .fc-button-primary:hover {
                background-color: #1d4ed8 !important;
                border-color: #1d4ed8 !important;
              }
              .fc .fc-button-primary:not(:disabled).fc-button-active {
                background-color: #1e40af !important;
                border-color: #1e40af !important;
              }
              .fc .fc-daygrid-day:hover {
                background-color: #f1f5f9;
              }
              .fc .fc-event {
                border-radius: 8px !important;
                padding: 2px 6px !important;
                font-size: 0.8rem !important;
                cursor: pointer !important;
              }
              .fc .fc-event:hover {
                opacity: 0.9;
                transform: scale(1.02);
                transition: all 0.2s ease;
              }
              .fc .fc-daygrid-day-number {
                font-weight: 600;
                color: #334155;
              }
              .fc .fc-col-header-cell-cushion {
                font-weight: 700;
                color: #1e293b;
                text-transform: uppercase;
                font-size: 0.75rem;
              }
              .fc-theme-standard td, .fc-theme-standard th {
                border-color: #e2e8f0 !important;
              }
              .fc .fc-timegrid-slot {
                height: 40px !important;
              }
              .fc .fc-timegrid-slot-label {
                font-size: 0.75rem;
                color: #64748b;
              }
            `}
          </style>
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            headerToolbar={compact ? {
              left: 'prev,next',
              center: 'title',
              right: ''
            } : {
              left: 'prev,next today',
              center: 'title',
              right: 'dayGridMonth,timeGridWeek,timeGridDay'
            }}
            locale="fr"
            buttonText={{
              today: "Aujourd'hui",
              month: 'Mois',
              week: 'Semaine',
              day: 'Jour'
            }}
            events={events}
            eventClick={handleEventClick}
            selectable={userRole === 'admin' || userRole === 'referent' || userRole === 'animateur'}
            select={handleDateSelect}
            dateClick={handleDateClick}
            editable={!compact && (userRole === 'admin' || userRole === 'animateur')}
            eventDrop={handleEventDrop}
            droppable={!compact && (userRole === 'admin' || userRole === 'animateur')}
            height={compact ? "350px" : "auto"}
            aspectRatio={compact ? 1.2 : 1.8}
            firstDay={1}
            eventTimeFormat={{
              hour: '2-digit',
              minute: '2-digit',
              hour12: false
            }}
            slotMinTime="07:00:00"
            slotMaxTime="20:00:00"
            allDaySlot={false}
            nowIndicator={true}
            dayMaxEvents={compact ? 2 : 3}
            moreLinkText={(num) => `+${num} autres`}
            fixedWeekCount={false}
            showNonCurrentDates={false}
          />
        </CardContent>
      </Card>

      {/* Dialog Détail Événement */}
      <Dialog open={showEventDialog} onOpenChange={setShowEventDialog}>
        <DialogContent className="sm:max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
              {selectedEvent?.eventType === 'atelier' ? (
                <CalendarDays className="w-5 h-5 text-blue-600" />
              ) : (
                <Clock className="w-5 h-5 text-indigo-600" />
              )}
              {selectedEvent?.titre}
            </DialogTitle>
            <DialogDescription>
              {selectedEvent?.eventType === 'atelier' ? 'Atelier / Événement collectif' : 'Rendez-vous individuel'}
            </DialogDescription>
          </DialogHeader>
          
          {selectedEvent && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <span className="text-xs text-slate-500 font-medium">Date</span>
                  <p className="font-bold text-slate-900 flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-slate-400" />
                    {selectedEvent.date}
                  </p>
                </div>
                <div className="space-y-1">
                  <span className="text-xs text-slate-500 font-medium">Horaire</span>
                  <p className="font-bold text-slate-900 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-slate-400" />
                    {formatTime(selectedEvent.heure_debut)} - {formatTime(selectedEvent.heure_fin)}
                  </p>
                </div>
              </div>
              
              {selectedEvent.zone_geographique && (
                <div className="space-y-1">
                  <span className="text-xs text-slate-500 font-medium">Lieu</span>
                  <p className="font-medium text-slate-900 flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    {selectedEvent.zone_geographique}
                  </p>
                </div>
              )}
              
              {selectedEvent.eventType === 'atelier' && selectedEvent.type_atelier && (
                <div className="space-y-1">
                  <span className="text-xs text-slate-500 font-medium">Type</span>
                  <Badge className="bg-blue-100 text-blue-700">{selectedEvent.type_atelier}</Badge>
                </div>
              )}
              
              {selectedEvent.eventType === 'atelier' && selectedEvent.participants && (
                <div className="space-y-2">
                  <span className="text-xs text-slate-500 font-medium flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    Participants ({selectedEvent.participants.length})
                  </span>
                  <div className="flex flex-wrap gap-2">
                    {selectedEvent.participants.slice(0, 5).map((p, i) => (
                      <Badge key={i} variant="outline" className="text-xs">
                        {p.prenom} {p.nom}
                      </Badge>
                    ))}
                    {selectedEvent.participants.length > 5 && (
                      <Badge variant="outline" className="text-xs">
                        +{selectedEvent.participants.length - 5} autres
                      </Badge>
                    )}
                  </div>
                </div>
              )}
              
              {selectedEvent.description && (
                <div className="space-y-1">
                  <span className="text-xs text-slate-500 font-medium">Description</span>
                  <p className="text-sm text-slate-700 bg-slate-50 rounded-xl p-3">
                    {selectedEvent.description}
                  </p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="flex gap-2">
            {(userRole === 'admin' || userRole === 'referent') && (
              <Button
                variant="outline"
                onClick={handleDeleteEvent}
                className="text-red-600 border-red-200 hover:bg-red-50 rounded-xl"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setShowEventDialog(false)}
              className="rounded-xl"
            >
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Création Événement */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="sm:max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Nunito, sans-serif' }}>Nouvel événement</DialogTitle>
            <DialogDescription>Créez rapidement un nouvel événement</DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={newEvent.type} onValueChange={(val) => setNewEvent({ ...newEvent, type: val })}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="atelier">Atelier / Événement collectif</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Titre *</Label>
              <Input
                value={newEvent.titre}
                onChange={(e) => setNewEvent({ ...newEvent, titre: e.target.value })}
                placeholder="Nom de l'événement"
                className="rounded-xl"
              />
            </div>
            
            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={newEvent.date}
                  onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Début</Label>
                <Input
                  type="time"
                  value={newEvent.heure_debut}
                  onChange={(e) => setNewEvent({ ...newEvent, heure_debut: e.target.value })}
                  className="rounded-xl"
                />
              </div>
              <div className="space-y-2">
                <Label>Fin</Label>
                <Input
                  type="time"
                  value={newEvent.heure_fin}
                  onChange={(e) => setNewEvent({ ...newEvent, heure_fin: e.target.value })}
                  className="rounded-xl"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Zone</Label>
              <Select value={newEvent.zone_geographique} onValueChange={(val) => setNewEvent({ ...newEvent, zone_geographique: val })}>
                <SelectTrigger className="rounded-xl">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Saint-Pierre">Saint-Pierre</SelectItem>
                  <SelectItem value="Sainte-Marie">Sainte-Marie</SelectItem>
                  <SelectItem value="Saint-Denis">Saint-Denis</SelectItem>
                  <SelectItem value="Le Port">Le Port</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={newEvent.description}
                onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                placeholder="Description optionnelle..."
                className="rounded-xl"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="rounded-xl">
              Annuler
            </Button>
            <Button onClick={handleCreateEvent} className="bg-blue-600 hover:bg-blue-700 rounded-xl">
              Créer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarView;
